import React, { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import { Box, Button } from '@mui/material';

import RoomTypeImagesForm from '~/components/Common/Forms/RoomTypeImagesForm';
import { buttonMessages as baseButtonMessages, buttonStates } from '~/components/Common/Forms/states/submitButton';
import { FlatImageEditorHandle } from '~/components/Common/Images/types';

import { formatError } from '~/utils/errorFormatter';

const buttonMessages = { ...baseButtonMessages };
buttonMessages[buttonStates.default] = 'Save changes';

export interface PropertyImage {
  id: string;
  property_id: string;
  id_cloudinary_external: string;
  filename: string;
  title?: string;
  order: number;
  hidden?: boolean;
}

interface Props {
  images: Array<PropertyImage>;
  onImagesChange: (images: Array<PropertyImage>) => void;
  createImage: (image) => Promise<void>;
  updateImages: (images) => Promise<void>;
  deleteImage: (imageId: string) => Promise<void>;
  propertyId: string;
}

function ImagesForm(props: Props) {
  const [propertyImages, setPropertyImages] = useState<Array<PropertyImage>>([]);
  const propertyImagesRef = useRef<Array<PropertyImage>>([]);
  const imagesFormRef = useRef<FlatImageEditorHandle | null>(null);
  const [saveButtonState, setSaveButtonState] = useState(buttonStates.default);
  const isButtonStateSaving = saveButtonState === buttonStates.saving;

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const nextPropertyImages = [...props.images];
    propertyImagesRef.current = nextPropertyImages;
    setPropertyImages(nextPropertyImages);
  }, [props.images]);

  const sortImagesByOrder = (images) => {
    return [...images]
      .map((i) => ({
        ...i,
        publicImageId: i.id,
      }))
      .sort((a, b) => {
        if (a.order > b.order) {
          return 1;
        }
        if (a.order < b.order) {
          return -1;
        }
        return 0;
      });
  };

  const onCreateImage = async (cloudinaryId: string, filename: string) => {
    try {
      const createdImage = {
        id: uuid(),
        id_cloudinary_external: cloudinaryId,
        property_id: props.propertyId,
        filename,
        title: 'New image',
        order: propertyImages.length + 1,
      };
      await props.createImage(createdImage);
      const nextPropertyImages = propertyImages.concat(createdImage);
      propertyImagesRef.current = nextPropertyImages;
      setPropertyImages(nextPropertyImages);
      return createdImage;
    } catch (err) {
      enqueueSnackbar(formatError(err), {
        variant: 'error',
      });
    }
  };

  const onDeleteImage = async (propertyImageId: string) => {
    try {
      const newImages = propertyImages.filter((i) => i.id !== propertyImageId);
      for (let i = 0; i < newImages.length; i++) {
        newImages[i].order = i + 1;
      }
      propertyImagesRef.current = newImages;
      setPropertyImages(newImages);
      return props.deleteImage(propertyImageId);
    } catch (err) {
      enqueueSnackbar(formatError(err), {
        variant: 'error',
      });
    }
  };

  const onUpdateImages = async (images: Array<any>) => {
    setSaveButtonState(buttonStates.default);
    propertyImagesRef.current = images;
    setPropertyImages(images);
  };

  const onSaveImages = async () => {
    setSaveButtonState(buttonStates.saving);

    const uploadResult = await imagesFormRef.current?.uploadAllPending?.();

    if (uploadResult?.failedCount) {
      setSaveButtonState(buttonStates.failed);
      return;
    }

    const updatedImages = propertyImagesRef.current.map((i) => {
      const { id, id_cloudinary_external, title, order } = i;
      return {
        id,
        id_cloudinary_external,
        title,
        order,
      };
    });
    props
      .updateImages(updatedImages)
      .then(() => {
        setSaveButtonState(buttonStates.saved);
      })
      .catch(() => {
        setSaveButtonState(buttonStates.failed);
      });
  };

  return (
    <>
      <RoomTypeImagesForm
        ref={imagesFormRef}
        enablePendingCropUpload
        images={sortImagesByOrder(propertyImages)}
        hasHiddenToggle={false}
        onAddImage={onCreateImage}
        onUpdateImages={onUpdateImages}
        onDeleteImage={onDeleteImage}
        hideDeleteButton={false}
      />

      <Box mt={3}>
        <Button variant="contained" onClick={onSaveImages} disabled={isButtonStateSaving}>
          {buttonMessages[saveButtonState]}
        </Button>
      </Box>
    </>
  );
}

export default ImagesForm;
